import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormDraftContentDto, FormSummary } from '@munifocus/contracts-gov';
import { api } from './api-client';
import { formsKeys } from './forms-api';

/**
 * In-portal authoring: the draft's text in, edited runs out.
 *
 * The browser never touches the .docx package. It receives text and returns
 * text, and the one write that rebuilds the document happens on the server
 * against the stored original — see `ooxml-runs.ts` for why that is the whole
 * point rather than an implementation detail.
 */

export const authoringKeys = {
  runs: (formId: string) => ['forms', 'runs', formId] as const,
};

export function useDraftRuns(fileId: string, formId: string, enabled = true) {
  return useQuery({
    queryKey: authoringKeys.runs(formId),
    queryFn: async () => {
      const { data } = await api.get<FormDraftContentDto>(`/files/${fileId}/forms/${formId}/runs`);
      return data;
    },
    enabled: enabled && Boolean(formId),
    // Never served from cache: run indices address positions in a specific
    // stored document, and a draft recreated in another tab renumbers them.
    // A stale list would write this version's sentences into that one.
    staleTime: 0,
    gcTime: 0,
  });
}

export function useSaveDraftRuns(fileId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // The screen reports the failure itself.
    meta: { silent: true },
    mutationFn: async ({
      formId,
      runs,
      expectedUpdatedAt,
    }: {
      formId: string;
      runs: Record<string, string>;
      /** The `updatedAt` the runs were read at; a stale save is refused (409). */
      expectedUpdatedAt?: string;
    }) => {
      const { data } = await api.put<FormSummary>(`/files/${fileId}/forms/${formId}/runs`, {
        runs,
        expectedUpdatedAt,
      });
      return data;
    },
    onSuccess: (_summary, { formId }) => {
      void queryClient.invalidateQueries({ queryKey: authoringKeys.runs(formId) });
      // The saved draft's `updatedAt` moves, and the catalogue shows it.
      void queryClient.invalidateQueries({ queryKey: formsKeys.forFile(fileId) });
    },
  });
}
